import { format } from 'date-fns'
import type { BackupEnvelope, CaseSnapshot, EvidenceItem } from '../types'
import { db } from './db'
import { getPlaybook } from './playbooks'
import {
  blobToDataUrl,
  dataUrlToBlob,
  downloadBlob,
  escapeCsv,
  formatDate,
  formatMoney,
  hashBlob,
  sanitizeFilename,
} from './utils'

function caseMarkdown(snapshot: CaseSnapshot): string {
  const playbook = getPlaybook(snapshot.case.playbookId)
  const chronology = [...snapshot.events]
    .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))
    .map(
      (event, index) =>
        `${index + 1}. **${formatDate(event.occurredAt)} — ${event.title}**  \n` +
        `   ${event.actor}${event.channel ? ` · ${event.channel}` : ''}  \n` +
        `   ${event.detail}`,
    )
    .join('\n\n')
  const promises = snapshot.commitments.length
    ? snapshot.commitments
        .map((item) => {
          const sourceEvent = snapshot.events.find((event) => event.id === item.sourceEventId)
          const sourceEvidence = snapshot.evidence.find(
            (evidenceItem) => evidenceItem.id === item.sourceEvidenceId,
          )
          const history = (item.history ?? [])
            .map(
              (entry) =>
                `  - ${formatDate(entry.at)} · ${entry.status.toUpperCase()}${
                  entry.note ? ` — ${entry.note}` : ''
                }`,
            )
            .join('\n')
          return `- **${item.maker} · ${item.status.toUpperCase()} · due ${formatDate(item.dueAt)}** — ${item.detail}
  - Source event: ${
    sourceEvent
      ? `${formatDate(sourceEvent.occurredAt)} — ${sourceEvent.title}`
      : item.sourceEventId
        ? `Unavailable (retained event ID: \`${item.sourceEventId}\`)`
        : 'None linked'
  }
  - Supporting file: ${
    sourceEvidence
      ? `${sourceEvidence.name} — SHA-256 \`${sourceEvidence.hash}\``
      : item.sourceEvidenceRemoved
        ? `Removed ${formatDate(item.sourceEvidenceRemoved.removedAt)} — ${item.sourceEvidenceRemoved.name}; retained SHA-256 \`${item.sourceEvidenceRemoved.hash}\` (file ID: \`${item.sourceEvidenceRemoved.id}\`)`
        : 'None linked'
  }
  - Source excerpt: ${item.sourceExcerpt ? `“${item.sourceExcerpt}”` : 'Not recorded'}
  - Outcome history:
${history}`
        })
        .join('\n')
    : '- None recorded.'
  const evidence = snapshot.evidence.length
    ? snapshot.evidence
        .map(
          (item, index) =>
            `${index + 1}. **${item.name}** — ${item.category}; SHA-256 \`${item.hash}\`${
              item.note ? `; ${item.note}` : ''
            }`,
        )
        .join('\n')
    : 'No files recorded.'

  return `# ${snapshot.case.title}

Generated by Plaincase on ${format(new Date(), 'MMMM d, yyyy')}  
Case type: ${playbook.shortTitle}  
Status: ${snapshot.case.status}  
Counterparty: ${snapshot.case.counterparty || 'Not recorded'}  
Reference: ${snapshot.case.reference || 'Not recorded'}  
Amount involved (USD): ${formatMoney(snapshot.case.amount)}  
Target date: ${formatDate(snapshot.case.targetDate)}

## The matter

${snapshot.case.summary || 'No summary recorded.'}

## Desired resolution

${snapshot.case.goal || 'No goal recorded.'}

## Chronology

${chronology || 'No events recorded.'}

## Promise ledger

${promises}

## Evidence manifest

${evidence}

---

This is a user-created organizational record, not legal advice or an official filing. File
hashes are local integrity fingerprints: they can help identify whether a file changed after
it was added, but they are not a trusted timestamp, proof of authorship, or legal chain of custody.
Review the pack for private or sensitive information before sharing it.
`
}

function timelineCsv(snapshot: CaseSnapshot): string {
  const rows = [
    ['date', 'type', 'title', 'actor', 'channel', 'commitment_id', 'detail']
      .map(escapeCsv)
      .join(','),
    ...[...snapshot.events]
      .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))
      .map((event) =>
        [
          event.occurredAt,
          event.kind,
          event.title,
          event.actor,
          event.channel,
          event.commitmentId,
          event.detail,
        ]
          .map(escapeCsv)
          .join(','),
      ),
  ]
  return rows.join('\n')
}

function commitmentsCsv(snapshot: CaseSnapshot): string {
  const rows = [
    [
      'maker',
      'promise',
      'due',
      'status',
      'source_event_id',
      'source_event_date',
      'source_event_title',
      'source_file_id',
      'source_file',
      'source_file_sha256',
      'source_file_status',
      'source_file_removed_at',
      'source_excerpt',
      'status_history',
    ]
      .map(escapeCsv)
      .join(','),
    ...snapshot.commitments.map((item) => {
      const sourceEvent = snapshot.events.find((event) => event.id === item.sourceEventId)
      const sourceEvidence = snapshot.evidence.find(
        (evidenceItem) => evidenceItem.id === item.sourceEvidenceId,
      )
      return [
        item.maker,
        item.detail,
        item.dueAt,
        item.status,
        item.sourceEventId,
        sourceEvent?.occurredAt,
        sourceEvent?.title,
        item.sourceEvidenceId ?? item.sourceEvidenceRemoved?.id,
        sourceEvidence?.name ?? item.sourceEvidenceRemoved?.name,
        sourceEvidence?.hash ?? item.sourceEvidenceRemoved?.hash,
        sourceEvidence ? 'available' : item.sourceEvidenceRemoved ? 'removed' : 'not-linked',
        item.sourceEvidenceRemoved?.removedAt,
        item.sourceExcerpt,
        (item.history ?? [])
          .map((entry) => `${entry.at}: ${entry.status}${entry.note ? ` — ${entry.note}` : ''}`)
          .join(' | '),
      ]
        .map(escapeCsv)
        .join(',')
    }),
  ]
  return rows.join('\n')
}

export async function exportCasePack(snapshot: CaseSnapshot): Promise<void> {
  const { default: JSZip } = await import('jszip')
  const zip = new JSZip()
  const slug = sanitizeFilename(snapshot.case.title).toLowerCase() || 'plaincase'
  const root = zip.folder(slug)
  if (!root) throw new Error('Could not create case pack')

  root.file('case-brief.md', caseMarkdown(snapshot))
  root.file('timeline.csv', timelineCsv(snapshot))
  root.file('promise-ledger.csv', commitmentsCsv(snapshot))
  root.file(
    'integrity-manifest.json',
    JSON.stringify(
      {
        format: 'plaincase-integrity-manifest',
        version: 1,
        generatedAt: new Date().toISOString(),
        caseId: snapshot.case.id,
        hashAlgorithm: 'SHA-256',
        caveat:
          'Local fingerprints only; not trusted timestamps, proof of authorship, or a legal chain of custody.',
        files: snapshot.evidence.map(({ id, name, mimeType, size, category, hash, occurredAt }) => ({
          id,
          name,
          mimeType,
          size,
          category,
          hash,
          occurredAt,
        })),
      },
      null,
      2,
    ),
  )
  root.file(
    'README.txt',
    `PLAINCASE CASE PACK

Open case-brief.md for the human-readable summary.
The CSV files can be opened in any spreadsheet.
Evidence files are in the evidence folder.
integrity-manifest.json lists each file's SHA-256 fingerprint.

Before sharing:
- Check every file for private information.
- Remove anything irrelevant to the recipient.
- Confirm dates, names, and factual descriptions.
- Get qualified advice when the situation calls for it.

Plaincase does not give legal advice and this export is not an official filing.
`,
  )
  const evidenceFolder = root.folder('evidence')
  snapshot.evidence.forEach((item, index) => {
    const prefix = String(index + 1).padStart(2, '0')
    evidenceFolder?.file(`${prefix}-${sanitizeFilename(item.name)}`, item.blob)
  })

  const blob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  })
  downloadBlob(blob, `${slug}-case-pack.zip`)
}

function escapeIcs(text: string): string {
  return text
    .replaceAll('\\', '\\\\')
    .replace(/\r\n|\r|\n/g, '\\n')
    .replaceAll(',', '\\,')
    .replaceAll(';', '\\;')
}

function foldIcsLine(line: string): string {
  const encoder = new TextEncoder()
  const parts: string[] = []
  let current = ''
  let limit = 75
  for (const character of line) {
    if (current && encoder.encode(current + character).length > limit) {
      parts.push(current)
      current = character
      limit = 74
    } else {
      current += character
    }
  }
  parts.push(current)
  return parts.join('\r\n ')
}

function toIcsDate(value: string): string {
  return value.replace(/[-:]/g, '').replace(/\.\d{3}/, '')
}

function icsStart(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? `DTSTART;VALUE=DATE:${toIcsDate(value)}`
    : `DTSTART:${toIcsDate(value)}`
}

export function exportCalendar(snapshots: CaseSnapshot[]): void {
  const entries: Array<{ id: string; title: string; detail: string; date: string }> = []
  snapshots.forEach((snapshot) => {
    if (snapshot.case.status === 'resolved' || snapshot.case.status === 'archived') return
    if (snapshot.case.targetDate) {
      entries.push({
        id: `${snapshot.case.id}-target`,
        title: `Plaincase target: ${snapshot.case.title}`,
        detail: snapshot.case.goal,
        date: snapshot.case.targetDate,
      })
    }
    snapshot.commitments
      .filter((item) => item.status === 'open')
      .forEach((item) =>
        entries.push({
          id: item.id,
          title: `Promise due: ${item.maker}`,
          detail: `${snapshot.case.title} — ${item.detail}`,
          date: item.dueAt,
        }),
      )
    snapshot.tasks
      .filter((item) => item.status === 'open' && item.dueAt)
      .forEach((item) =>
        entries.push({
          id: item.id,
          title: `Plaincase: ${item.title}`,
          detail: snapshot.case.title,
          date: item.dueAt!,
        }),
      )
  })

  const now = toIcsDate(new Date().toISOString())
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Plaincase//Deadlines//EN',
    'CALSCALE:GREGORIAN',
    ...entries.flatMap((entry) => [
      'BEGIN:VEVENT',
      `UID:${escapeIcs(entry.id)}@plaincase.local`,
      `DTSTAMP:${now}`,
      icsStart(entry.date),
      `SUMMARY:${escapeIcs(entry.title)}`,
      `DESCRIPTION:${escapeIcs(entry.detail)}`,
      'END:VEVENT',
    ]),
    'END:VCALENDAR',
  ]
  const calendar = `${lines.map(foldIcsLine).join('\r\n')}\r\n`
  downloadBlob(new Blob([calendar], { type: 'text/calendar;charset=utf-8' }), 'plaincase-dates.ics')
}

export async function exportBackup(): Promise<void> {
  const [cases, events, evidence, commitments, tasks] = await Promise.all([
    db.cases.toArray(),
    db.events.toArray(),
    db.evidence.toArray(),
    db.commitments.toArray(),
    db.tasks.toArray(),
  ])
  const serializedEvidence = await Promise.all(
    evidence.map(async ({ blob, ...item }) => ({
      ...item,
      dataUrl: await blobToDataUrl(blob),
    })),
  )
  const envelope: BackupEnvelope = {
    format: 'plaincase-backup',
    version: 1,
    exportedAt: new Date().toISOString(),
    cases,
    events,
    evidence: serializedEvidence,
    commitments,
    tasks,
  }
  const stamp = format(new Date(), 'yyyy-MM-dd')
  downloadBlob(
    new Blob([JSON.stringify(envelope, null, 2)], { type: 'application/json' }),
    `plaincase-backup-${stamp}.json`,
  )
}

export class BackupError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'BackupError'
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function requireString(
  record: Record<string, unknown>,
  key: string,
  context: string,
  maxLength = 100_000,
): string {
  const value = record[key]
  if (typeof value !== 'string' || value.length > maxLength) {
    throw new BackupError(`Invalid backup: ${context}.${key} must be a string.`)
  }
  return value
}

function optionalString(
  record: Record<string, unknown>,
  key: string,
  context: string,
  maxLength = 100_000,
): string | undefined {
  const value = record[key]
  if (value === undefined) return undefined
  if (typeof value !== 'string' || value.length > maxLength) {
    throw new BackupError(`Invalid backup: ${context}.${key} must be a string when present.`)
  }
  return value
}

type DateShape = 'date' | 'timestamp' | 'either'

function isCalendarDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return false
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const candidate = new Date(Date.UTC(year, month - 1, day))
  return (
    candidate.getUTCFullYear() === year &&
    candidate.getUTCMonth() === month - 1 &&
    candidate.getUTCDate() === day
  )
}

function isCanonicalTimestamp(value: string): boolean {
  const parsed = new Date(value)
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString() === value
}

function requireDate(
  value: string | undefined,
  context: string,
  shape: DateShape = 'either',
): void {
  if (value === undefined) return
  const valid =
    (shape !== 'timestamp' && isCalendarDate(value)) ||
    (shape !== 'date' && isCanonicalTimestamp(value))
  if (!valid) {
    throw new BackupError(
      `Invalid backup: ${context} must be a real ${
        shape === 'date'
          ? 'YYYY-MM-DD calendar date'
          : shape === 'timestamp'
            ? 'canonical ISO timestamp'
            : 'calendar date or canonical ISO timestamp'
      }.`,
    )
  }
}

function validateIds(items: unknown[], context: string): Set<string> {
  const ids = new Set<string>()
  items.forEach((item, index) => {
    if (!isRecord(item)) throw new BackupError(`Invalid backup: ${context}[${index}] is not an object.`)
    const id = requireString(item, 'id', `${context}[${index}]`, 200)
    if (!/^[A-Za-z0-9_.-]+$/.test(id) || ids.has(id)) {
      throw new BackupError(
        `Invalid backup: ${context} contains an unsafe, empty, or duplicate ID.`,
      )
    }
    ids.add(id)
  })
  return ids
}

function assertBackup(value: unknown): asserts value is BackupEnvelope {
  if (!isRecord(value)) throw new BackupError('That file is not a Plaincase backup.')
  if (value.format !== 'plaincase-backup' || value.version !== 1) {
    throw new BackupError('This backup format is not supported.')
  }

  const limits = {
    cases: 5_000,
    events: 100_000,
    evidence: 10_000,
    commitments: 50_000,
    tasks: 50_000,
  }
  for (const [key, limit] of Object.entries(limits)) {
    const collection = value[key]
    if (!Array.isArray(collection) || collection.length > limit) {
      throw new BackupError(`Invalid backup: ${key} must be an array with at most ${limit} entries.`)
    }
  }

  const candidate = value as Record<string, unknown[]> & Record<string, unknown>
  const caseIds = validateIds(candidate.cases, 'cases')
  validateIds(candidate.events, 'events')
  validateIds(candidate.evidence, 'evidence')
  const commitmentIds = validateIds(candidate.commitments, 'commitments')
  validateIds(candidate.tasks, 'tasks')
  const caseByEvent = new Map<string, string>()
  const caseByEvidence = new Map<string, string>()
  const caseByCommitment = new Map<string, string>()
  for (const [items, context, target] of [
    [candidate.events, 'events', caseByEvent],
    [candidate.evidence, 'evidence', caseByEvidence],
    [candidate.commitments, 'commitments', caseByCommitment],
  ] as const) {
    items.forEach((item, index) => {
      if (!isRecord(item)) return
      target.set(
        requireString(item, 'id', `${context}[${index}]`, 200),
        requireString(item, 'caseId', `${context}[${index}]`, 200),
      )
    })
  }
  const exportedAt = requireString(candidate, 'exportedAt', 'backup', 40)
  requireDate(exportedAt, 'backup.exportedAt', 'timestamp')

  const statuses = new Set(['active', 'waiting', 'resolved', 'archived'])
  const stages = new Set(['capture', 'contact', 'follow-up', 'escalate', 'resolved'])
  candidate.cases.forEach((item, index) => {
    if (!isRecord(item)) return
    const context = `cases[${index}]`
    requireString(item, 'title', context, 500)
    requireString(item, 'playbookId', context, 100)
    requireString(item, 'summary', context)
    requireString(item, 'goal', context)
    requireString(item, 'counterparty', context, 500)
    optionalString(item, 'reference', context, 500)
    const status = requireString(item, 'status', context, 20)
    const stage = requireString(item, 'stage', context, 20)
    if (!statuses.has(status) || !stages.has(stage)) {
      throw new BackupError(`Invalid backup: ${context} has an unknown status or stage.`)
    }
    const amount = item.amount
    if (
      amount !== undefined &&
      (typeof amount !== 'number' || !Number.isFinite(amount) || amount < 0)
    ) {
      throw new BackupError(`Invalid backup: ${context}.amount must be a non-negative number.`)
    }
    const accent = requireString(item, 'accent', context, 20)
    if (!/^#[0-9a-f]{6}$/i.test(accent)) {
      throw new BackupError(`Invalid backup: ${context}.accent is not a six-digit hex color.`)
    }
    const targetDate = optionalString(item, 'targetDate', context, 40)
    const createdAt = requireString(item, 'createdAt', context, 40)
    const updatedAt = requireString(item, 'updatedAt', context, 40)
    requireDate(targetDate, `${context}.targetDate`, 'date')
    requireDate(createdAt, `${context}.createdAt`, 'timestamp')
    requireDate(updatedAt, `${context}.updatedAt`, 'timestamp')
  })

  const eventKinds = new Set([
    'note',
    'message',
    'call',
    'meeting',
    'payment',
    'incident',
    'deadline',
  ])
  candidate.events.forEach((item, index) => {
    if (!isRecord(item)) return
    const context = `events[${index}]`
    const caseId = requireString(item, 'caseId', context, 200)
    if (!caseIds.has(caseId)) throw new BackupError(`Invalid backup: ${context} refers to no case.`)
    const kind = requireString(item, 'kind', context, 20)
    if (!eventKinds.has(kind)) throw new BackupError(`Invalid backup: ${context} has an unknown kind.`)
    requireString(item, 'title', context, 500)
    requireString(item, 'detail', context)
    requireString(item, 'actor', context, 500)
    optionalString(item, 'channel', context, 500)
    const commitmentId = optionalString(item, 'commitmentId', context, 200)
    if (commitmentId) {
      if (!commitmentIds.has(commitmentId)) {
        throw new BackupError(`Invalid backup: ${context} refers to no commitment.`)
      }
      if (caseByCommitment.get(commitmentId) !== caseId) {
        throw new BackupError(`Invalid backup: ${context} links to a commitment in another case.`)
      }
    }
    const occurredAt = requireString(item, 'occurredAt', context, 40)
    const createdAt = requireString(item, 'createdAt', context, 40)
    requireDate(occurredAt, `${context}.occurredAt`, 'timestamp')
    requireDate(createdAt, `${context}.createdAt`, 'timestamp')
  })

  candidate.evidence.forEach((item, index) => {
    if (!isRecord(item)) return
    const context = `evidence[${index}]`
    const caseId = requireString(item, 'caseId', context, 200)
    if (!caseIds.has(caseId)) throw new BackupError(`Invalid backup: ${context} refers to no case.`)
    requireString(item, 'name', context, 500)
    requireString(item, 'mimeType', context, 300)
    requireString(item, 'category', context, 100)
    optionalString(item, 'note', context)
    const size = item.size
    if (
      typeof size !== 'number' ||
      !Number.isSafeInteger(size) ||
      size < 0 ||
      size > 50 * 1024 * 1024
    ) {
      throw new BackupError(`Invalid backup: ${context}.size is outside the 50 MB file limit.`)
    }
    const hash = requireString(item, 'hash', context, 64)
    if (!/^[0-9a-f]{64}$/i.test(hash)) {
      throw new BackupError(`Invalid backup: ${context}.hash is not SHA-256.`)
    }
    const dataUrl = requireString(item, 'dataUrl', context, 70 * 1024 * 1024)
    if (!/^data:[^,;\r\n]*;base64,[A-Za-z0-9+/]*={0,2}$/.test(dataUrl)) {
      throw new BackupError(`Invalid backup: ${context}.dataUrl is not base64 file data.`)
    }
    const addedAt = requireString(item, 'addedAt', context, 40)
    const occurredAt = optionalString(item, 'occurredAt', context, 40)
    requireDate(addedAt, `${context}.addedAt`, 'timestamp')
    requireDate(occurredAt, `${context}.occurredAt`, 'date')
  })

  const commitmentStatuses = new Set(['open', 'kept', 'missed'])
  candidate.commitments.forEach((item, index) => {
    if (!isRecord(item)) return
    const context = `commitments[${index}]`
    const caseId = requireString(item, 'caseId', context, 200)
    if (!caseIds.has(caseId)) throw new BackupError(`Invalid backup: ${context} refers to no case.`)
    requireString(item, 'maker', context, 500)
    requireString(item, 'detail', context)
    optionalString(item, 'sourceExcerpt', context)
    const sourceEventId = optionalString(item, 'sourceEventId', context, 200)
    const sourceEvidenceId = optionalString(item, 'sourceEvidenceId', context, 200)
    if (sourceEventId) {
      if (!caseByEvent.has(sourceEventId)) {
        throw new BackupError(`Invalid backup: ${context} refers to no source event.`)
      }
      if (caseByEvent.get(sourceEventId) !== caseId) {
        throw new BackupError(`Invalid backup: ${context} links to a source event in another case.`)
      }
    }
    if (sourceEvidenceId) {
      if (!caseByEvidence.has(sourceEvidenceId)) {
        throw new BackupError(`Invalid backup: ${context} refers to no source file.`)
      }
      if (caseByEvidence.get(sourceEvidenceId) !== caseId) {
        throw new BackupError(`Invalid backup: ${context} links to a source file in another case.`)
      }
    }
    const removedSource = item.sourceEvidenceRemoved
    if (removedSource !== undefined) {
      if (!isRecord(removedSource) || sourceEvidenceId) {
        throw new BackupError(
          `Invalid backup: ${context}.sourceEvidenceRemoved is malformed or conflicts with a live source.`,
        )
      }
      const removedId = requireString(removedSource, 'id', `${context}.sourceEvidenceRemoved`, 200)
      if (!/^[A-Za-z0-9_.-]+$/.test(removedId)) {
        throw new BackupError(`Invalid backup: ${context}.sourceEvidenceRemoved has an unsafe ID.`)
      }
      requireString(removedSource, 'name', `${context}.sourceEvidenceRemoved`, 500)
      const removedHash = requireString(
        removedSource,
        'hash',
        `${context}.sourceEvidenceRemoved`,
        64,
      )
      if (!/^[0-9a-f]{64}$/i.test(removedHash)) {
        throw new BackupError(
          `Invalid backup: ${context}.sourceEvidenceRemoved.hash is not SHA-256.`,
        )
      }
      const removedAt = requireString(
        removedSource,
        'removedAt',
        `${context}.sourceEvidenceRemoved`,
        40,
      )
      requireDate(removedAt, `${context}.sourceEvidenceRemoved.removedAt`, 'timestamp')
    }
    const status = requireString(item, 'status', context, 20)
    if (!commitmentStatuses.has(status)) {
      throw new BackupError(`Invalid backup: ${context} has an unknown status.`)
    }
    const dueAt = requireString(item, 'dueAt', context, 40)
    const createdAt = requireString(item, 'createdAt', context, 40)
    requireDate(dueAt, `${context}.dueAt`, 'date')
    requireDate(createdAt, `${context}.createdAt`, 'timestamp')
    const history = item.history
    if (history !== undefined) {
      if (!Array.isArray(history) || history.length > 1_000) {
        throw new BackupError(`Invalid backup: ${context}.history is too large or malformed.`)
      }
      const historyIds = new Set<string>()
      history.forEach((entry, historyIndex) => {
        if (!isRecord(entry)) {
          throw new BackupError(`Invalid backup: ${context}.history[${historyIndex}] is malformed.`)
        }
        const historyId = requireString(entry, 'id', `${context}.history[${historyIndex}]`, 200)
        if (!/^[A-Za-z0-9_.-]+$/.test(historyId) || historyIds.has(historyId)) {
          throw new BackupError(`Invalid backup: ${context}.history contains an unsafe or duplicate ID.`)
        }
        historyIds.add(historyId)
        const historyStatus = requireString(
          entry,
          'status',
          `${context}.history[${historyIndex}]`,
          20,
        )
        if (!commitmentStatuses.has(historyStatus)) {
          throw new BackupError(`Invalid backup: ${context}.history has an unknown status.`)
        }
        const at = requireString(entry, 'at', `${context}.history[${historyIndex}]`, 40)
        optionalString(entry, 'note', `${context}.history[${historyIndex}]`)
        requireDate(at, `${context}.history[${historyIndex}].at`, 'timestamp')
      })
    }
  })

  candidate.tasks.forEach((item, index) => {
    if (!isRecord(item)) return
    const context = `tasks[${index}]`
    const caseId = requireString(item, 'caseId', context, 200)
    if (!caseIds.has(caseId)) throw new BackupError(`Invalid backup: ${context} refers to no case.`)
    requireString(item, 'title', context, 500)
    const status = requireString(item, 'status', context, 20)
    if (status !== 'open' && status !== 'done') {
      throw new BackupError(`Invalid backup: ${context} has an unknown status.`)
    }
    const dueAt = optionalString(item, 'dueAt', context, 40)
    const completedAt = optionalString(item, 'completedAt', context, 40)
    const createdAt = requireString(item, 'createdAt', context, 40)
    requireDate(dueAt, `${context}.dueAt`, 'date')
    requireDate(completedAt, `${context}.completedAt`, 'timestamp')
    requireDate(createdAt, `${context}.createdAt`, 'timestamp')
  })
}

export async function restoreBackup(file: File): Promise<void> {
  if (file.size > 250 * 1024 * 1024) {
    throw new BackupError('This backup is larger than the 250 MB browser restore limit.')
  }
  let raw: unknown
  try {
    raw = JSON.parse(await file.text()) as unknown
  } catch {
    throw new BackupError('That file could not be read as a Plaincase backup.')
  }
  assertBackup(raw)
  const evidence: EvidenceItem[] = []
  for (const { dataUrl, ...item } of raw.evidence) {
    let blob: Blob
    try {
      blob = dataUrlToBlob(dataUrl)
    } catch {
      throw new BackupError(`The stored file data for “${item.name}” is not readable.`)
    }
    if (blob.size !== item.size) {
      throw new BackupError(`Size check failed for “${item.name}”. The backup was not restored.`)
    }
    if (blob.type.toLowerCase() !== item.mimeType.toLowerCase()) {
      throw new BackupError(`File type check failed for “${item.name}”. The backup was not restored.`)
    }
    const actualHash = await hashBlob(blob)
    if (item.hash && actualHash !== item.hash) {
      throw new BackupError(`Integrity check failed for “${item.name}”. The backup was not restored.`)
    }
    evidence.push({ ...item, hash: actualHash, blob })
  }
  const commitments = raw.commitments.map((item) => ({
    ...item,
    history:
      Array.isArray(item.history) && item.history.length > 0
        ? item.history
        : [
            {
              id: `history_imported_${item.id}`,
              status: item.status,
              at: item.createdAt,
              note: 'Imported from an older Plaincase backup.',
            },
          ],
  }))

  await db.transaction(
    'rw',
    [db.cases, db.events, db.evidence, db.commitments, db.tasks, db.meta],
    async () => {
      await Promise.all([
        db.cases.clear(),
        db.events.clear(),
        db.evidence.clear(),
        db.commitments.clear(),
        db.tasks.clear(),
      ])
      await db.cases.bulkAdd(raw.cases)
      await db.events.bulkAdd(raw.events)
      await db.evidence.bulkAdd(evidence)
      await db.commitments.bulkAdd(commitments)
      await db.tasks.bulkAdd(raw.tasks)
      await db.meta.put({ key: 'initialized', value: 'restored' })
    },
  )
}

export function downloadEvidence(item: EvidenceItem): void {
  downloadBlob(item.blob, sanitizeFilename(item.name))
}
